import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Clock, TrendingUp, Lock } from 'lucide-react';
import { StoryPlayer } from '../components/StoryPlayer';
import { useProgress } from '../context/ProgressContext';
import { stories, getStoriesForAge } from '../data/storiesData';
export function StoriesPage() {
  const navigate = useNavigate();
  const [selectedStory, setSelectedStory] = useState<string | null>(null);
  const {
    progress
  } = useProgress();
  const userAge = progress.preferences.age || 16;
  const availableStories = getStoriesForAge(userAge);
  const lockedStories = stories.filter(story => userAge < story.ageRange[0] || userAge > story.ageRange[1]);
  const getCategoryColor = (category: string) => {
    const colors = {
      consent: 'from-purple-500 to-pink-500',
      relationships: 'from-rose-500 to-red-500',
      safety: 'from-orange-500 to-amber-500',
      health: 'from-teal-500 to-cyan-500',
      emotions: 'from-blue-500 to-indigo-500'
    };
    return colors[category as keyof typeof colors] || 'from-gray-500 to-slate-500';
  };
  const getDifficultyBadge = (difficulty: string) => {
    const badges = {
      easy: {
        color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        label: 'Beginner Friendly'
      },
      medium: {
        color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        label: 'Intermediate'
      },
      hard: {
        color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        label: 'Advanced'
      }
    };
    return badges[difficulty as keyof typeof badges] || badges.medium;
  };
  if (selectedStory) {
    return <StoryPlayer storyId={selectedStory} onExit={() => setSelectedStory(null)} data-id="element-1015" />;
  }
  return <div className="min-h-screen w-full bg-gradient-to-br from-teal-50 to-green-50 dark:from-slate-900 dark:via-teal-950 dark:to-green-950 pb-20 md:pb-8" data-id="element-1016">
      {/* Header */}
      <header className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-b border-teal-100 dark:border-slate-700 sticky top-0 z-10" data-id="element-1017">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between" data-id="element-1018">
          <button onClick={() => navigate('/home')} className="flex items-center space-x-2 text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300" data-id="element-1019">
            <ArrowLeft className="w-5 h-5" data-id="element-1020" />
            <span className="font-medium" data-id="element-1021">Back</span>
          </button>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-1022">
            Interactive Stories
          </h1>
          <div className="w-20" data-id="element-1023" />
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 py-8" data-id="element-1024">
        <div className="text-center mb-8" data-id="element-1025">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3" data-id="element-1026">
            Choose Your Adventure! 📖
          </h2>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" data-id="element-1027">
            Make choices and learn through real-life scenarios. Every decision
            teaches you something valuable!
          </p>
        </div>
        {/* Available Stories */}
        <div className="mb-12" data-id="element-1028">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center space-x-2" data-id="element-1029">
            <BookOpen className="w-6 h-6 text-teal-500" data-id="element-1030" />
            <span data-id="element-1031">Stories For You (Age {userAge})</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1032">
            {availableStories.map(story => {
            const isCompleted = progress.completedStories.includes(story.id);
            const difficultyBadge = getDifficultyBadge(story.difficulty);
            return <button key={story.id} onClick={() => setSelectedStory(story.id)} className="group bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all text-left transform hover:-translate-y-2 border-2 border-transparent hover:border-teal-200 dark:hover:border-teal-700 relative overflow-hidden" data-id="element-1033">
                  {isCompleted && <div className="absolute top-4 right-4 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center shadow-lg" data-id="element-1034">
                      <span className="text-white text-xl" data-id="element-1035">✓</span>
                    </div>}
                  <div className={`w-16 h-16 bg-gradient-to-br ${getCategoryColor(story.category)} rounded-2xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`} data-id="element-1036">
                    <BookOpen className="w-9 h-9 text-white" data-id="element-1037" />
                  </div>
                  <div className="flex items-center space-x-2 mb-3" data-id="element-1038">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${difficultyBadge.color}`} data-id="element-1039">
                      {difficultyBadge.label}
                    </span>
                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400" data-id="element-1040">
                      <Clock className="w-3 h-3 mr-1" data-id="element-1041" />
                      <span data-id="element-1042">{story.estimatedTime} min</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors" data-id="element-1043">
                    {story.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm" data-id="element-1044">
                    {story.description}
                  </p>
                  <div className="flex items-center text-sm font-semibold text-teal-600 dark:text-teal-400 group-hover:text-teal-700 dark:group-hover:text-teal-300" data-id="element-1045">
                    {isCompleted ? 'Play Again' : 'Start Story'}{' '}
                    <span className="ml-2 group-hover:translate-x-2 transition-transform" data-id="element-1046">
                      →
                    </span>
                  </div>
                </button>;
          })}
          </div>
        </div>
        {/* Locked Stories */}
        {lockedStories.length > 0 && <div data-id="element-1047">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center space-x-2" data-id="element-1048">
              <Lock className="w-6 h-6 text-gray-400" data-id="element-1049" />
              <span data-id="element-1050">Coming Soon (Other Age Groups)</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1051">
              {lockedStories.slice(0, 3).map(story => <div key={story.id} className="bg-gray-100 dark:bg-slate-800/50 rounded-2xl p-6 opacity-60 relative" data-id="element-1052">
                  <div className="absolute top-4 right-4" data-id="element-1053">
                    <Lock className="w-6 h-6 text-gray-400" data-id="element-1054" />
                  </div>
                  <div className="w-16 h-16 bg-gray-300 dark:bg-slate-700 rounded-2xl flex items-center justify-center mb-4" data-id="element-1055">
                    <BookOpen className="w-9 h-9 text-gray-500" data-id="element-1056" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-700 dark:text-gray-400 mb-2" data-id="element-1057">
                    {story.title}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-500 text-sm mb-4" data-id="element-1058">
                    {story.description}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 font-medium" data-id="element-1059">
                    Available for ages {story.ageRange[0]}-{story.ageRange[1]}
                  </p>
                </div>)}
            </div>
          </div>}
      </div>
    </div>;
}